import AppContext from "PFApp/app_context";
import FiltersBox from "PFApp/components/filters/filters_box";
import FiltersButton from "PFApp/components/filters/filters_button";
import FiltersPreview from "PFApp/components/filters/filters_preview";
import Toggle from "PFComponents/toggle/toggle";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { FeatureFlag, Filters, Meta } from "PFTypes";
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getValueFilters, handleRequestFiltersChange } from "../../../use_filtered_collection/format_filters";
import { useFilters } from "../../hooks/use_filters";
import { DefaultFilters, VisibleCategory } from "../types/types";
import css from "./filters_booking_module.module.scss";
import QuickDateFilter from "./quick_date_filter";
import SelectTilesToRender from "./supply_section/charts/tiles_section/select_tiles_to_render";
import { Utilization } from "./supply_section/supply_section";

type FiltersBookingModuleProps = {
  setFilters: (value: {}) => void;
  defaultFilters: DefaultFilters;
  setUtilization: Dispatch<SetStateAction<string>>;
  utilizationOption: string;
  visibleCategories: VisibleCategory[];
  setVisibleCategories: Dispatch<SetStateAction<VisibleCategory[]>>;
  tab: string;
  demandFilters?: {
    filters: Filters;
  };
};

const FiltersBookingModule = ({
  setFilters,
  defaultFilters,
  setUtilization,
  utilizationOption,
  visibleCategories,
  setVisibleCategories,
  tab,
  demandFilters
}: FiltersBookingModuleProps): JSX.Element => {
  const {
    store: { customTypes, currentProfile }
  } = useContext(AppContext);
  const { t } = useTranslation("insights");
  const showUtilizationMetrics = useIsFeatureEnabled()(FeatureFlag.ShowUtilization);

  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);

  const [selectedFilters, setSelectedFilters] = useState<DefaultFilters>(defaultFilters);
  const [meta, setMeta] = useState<Meta>({ filters: {}, total: 0 });

  // this is for supply, using the standard filters
  const insightsMeta = useFilters({
    customTypes: customTypes,
    selectedFilters: selectedFilters,
    currentProfile: currentProfile,
    tab: "bookingReports"
  });

  useEffect(() => {
    // @ts-ignore
    setMeta(insightsMeta);
  }, [tab, demandFilters, insightsMeta, selectedFilters]);

  const handleFilterChange = (filter, value) => {
    setSelectedFilters(() => {
      const valueFilters = getValueFilters(insightsMeta.filters);
      const newFilters = handleRequestFiltersChange(valueFilters, filter, value);
      return newFilters;
    });
  };

  useEffect(() => {
    setFilters(selectedFilters);
  }, [selectedFilters]);

  const handleFiltersReset = () => setSelectedFilters(defaultFilters);

  const noFiltersSelected = Object.keys(selectedFilters).length === 0;

  return (
    <>
      <div className={css.filters}>
        <div className={css.filtersWrap}>
          {(!!insightsMeta.filters || demandFilters) && (
            <div className={css.filtersPreview}>
              <FiltersPreview
                meta={meta}
                onMoreClicked={() => {}}
                blockedList={["_term"]}
                filtersAreClear={noFiltersSelected}
                clearFilters={handleFiltersReset}
                onFilterChange={handleFilterChange}
              />
            </div>
          )}

          <div className={css.filtersActions}>
            <QuickDateFilter
              availabilityFilter={meta.filters?.availability}
              handleFilterChange={handleFilterChange}
              dateRangeMode={tab === "supply" ? "future" : "all"}
            />
            <FiltersButton
              filtersShown={filtersOpen}
              filtersAreClear={noFiltersSelected}
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={css.filterButton}
            />
            {visibleCategories && (
              <SelectTilesToRender
                tilesCategories={visibleCategories}
                setVisibleCategories={setVisibleCategories}
              />
            )}
          </div>
        </div>
        {showUtilizationMetrics && utilizationOption && (
          <div>
            {t("bookingReports.totalUtilization")}
            <Toggle
              onChange={(value) => setUtilization(value ? Utilization.Billable : Utilization.Total)}
              inline
              checked={utilizationOption === Utilization.Billable}
            />
            {t("bookingReports.billableUtilization")}
          </div>
        )}
      </div>

      {filtersOpen && meta?.filters && tab === "supply" && (
        <div className={css.panel}>
          <FiltersBox
            style={{ position: "absolute", width: "100%", zIndex: 1000, top: -60 }}
            // @ts-ignore
            meta={meta}
            handleFilterChange={handleFilterChange}
            clearFilters={handleFiltersReset}
            closeFilters={() => setFiltersOpen(false)}
          />
        </div>
      )}
    </>
  );
};

export default FiltersBookingModule;
